import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Camera } from "lucide-react";
import { toast } from "sonner";
import SerialScanner from "@/components/SerialScanner";

const BLANK = {
  asset_tag: "", name: "", category: "Laptop", serial_number: "",
  manufacturer: "", model: "", status: "in_use", location: "",
  assigned_to: "", assigned_email: "", purchase_date: "", warranty_expiry: "",
  purchase_cost: 0, operating_system: "", cpu: "", ram: "", hdd: "",
  depreciation_type: "", depreciation_rate: 0,
  eoy1: 0, eoy2: 0, eoy3: 0, eoy4: 0,
  notes: "",
};

const DEPRECIATION_TYPES = ["Straight Line", "Diminishing Value", "Double Declining", "MACRS", "None"];

export default function AssetForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(BLANK);
  const [categories, setCategories] = useState([]);
  const [osList, setOsList] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [scanOpen, setScanOpen] = useState(false);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/operating-systems").then(({ data }) => setOsList(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      api
        .get(`/assets/${id}`)
        .then(({ data }) => setForm({ ...BLANK, ...data, purchase_cost: data.purchase_cost || 0 }))
        .catch(() => toast.error("Failed to load asset"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-calculate EOY1-4 from purchase price + depreciation rate (Diminishing Value)
  const calculatedEoy = useMemo(() => {
    const price = Number(form.purchase_cost) || 0;
    const rate = Number(form.depreciation_rate) || 0;
    const multiplier = 1 - rate / 100;
    const eoy1 = price * multiplier;
    const eoy2 = eoy1 * multiplier;
    const eoy3 = eoy2 * multiplier;
    const eoy4 = eoy3 * multiplier;
    return {
      eoy1: Math.round(eoy1 * 100) / 100,
      eoy2: Math.round(eoy2 * 100) / 100,
      eoy3: Math.round(eoy3 * 100) / 100,
      eoy4: Math.round(eoy4 * 100) / 100,
    };
  }, [form.purchase_cost, form.depreciation_rate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {
        ...form,
        purchase_cost: Number(form.purchase_cost) || 0,
        depreciation_rate: Number(form.depreciation_rate) || 0,
        eoy1: calculatedEoy.eoy1,
        eoy2: calculatedEoy.eoy2,
        eoy3: calculatedEoy.eoy3,
        eoy4: calculatedEoy.eoy4,
      };
      if (isEdit) {
        await api.put(`/assets/${id}`, payload);
        toast.success("Asset updated");
      } else {
        const { data } = await api.post("/assets", payload);
        toast.success("Asset created");
        navigate(`/assets/${data.id}`);
        return;
      }
      navigate(`/assets/${id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setError(typeof msg === "string" ? msg : "Failed to save");
      toast.error(typeof msg === "string" ? msg : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 font-mono text-xs text-[#7A7A7A]">Loading…</div>;

  return (
    <div data-testid="asset-form-page">
      <SerialScanner
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onDetected={({ serial, manufacturer, model }) => {
          setForm((prev) => ({
            ...prev,
            serial_number: serial || prev.serial_number,
            manufacturer: (!prev.manufacturer && manufacturer) ? manufacturer : prev.manufacturer,
            model: (!prev.model && model) ? model : prev.model,
          }));
        }}
      />
      <PageHeader
        eyebrow={isEdit ? "[02.2] Modify" : "[02.1] Register"}
        title={isEdit ? "Edit Asset" : "New Asset"}
        subtitle={isEdit ? `Update the record for ${form.asset_tag}.` : "Register a new device into the inventory."}
        actions={
          <Link to="/assets" data-testid="back-to-assets" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#5A5A5A] hover:text-[#0F0F0F]">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
        }
      />
      <form onSubmit={onSubmit} className="p-4 sm:p-6 lg:p-8 max-w-4xl space-y-6 sm:space-y-8" data-testid="asset-form">
        <Section title="Identification">
          <Field label="Asset Tag">
            <Input data-testid="input-asset_tag" value={form.asset_tag} onChange={(e) => set("asset_tag", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Splashtop Name *">
            <Input data-testid="input-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Category">
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="select-category" className={selectCls}><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none bg-white border-[#D6CFC1]">
                {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="select-status" className={selectCls}><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none bg-white border-[#D6CFC1]">
                <SelectItem value="in_use">In Use</SelectItem>
                <SelectItem value="storage">Storage</SelectItem>
                <SelectItem value="repair">Repair</SelectItem>
                <SelectItem value="retired" disabled={!isEdit}>Retired</SelectItem>
              </SelectContent>
            </Select>
            {form.status === "retired" && (
              <div className="mt-2 text-[10px] font-mono text-[#B45309] leading-relaxed">
                Retirement requires a destruction record. Save your edits, then click <strong>Retire Asset</strong> on the asset&apos;s detail page.
              </div>
            )}
          </Field>
          <Field label="Serial Number">
            <div className="flex items-center gap-2">
              <Input data-testid="input-serial_number" value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} className={inputCls} />
              <button
                type="button"
                data-testid="scan-serial-btn"
                onClick={() => setScanOpen(true)}
                title="Scan serial with camera"
                className="shrink-0 inline-flex items-center gap-1.5 px-3 h-[42px] rounded-none border border-[#D6CFC1] bg-[#F0EDE3] text-[#3A3A3A] hover:bg-[#E8E3D6] hover:border-[#1A1A1A] font-mono text-[10px] uppercase tracking-wider transition-colors"
              >
                <Camera className="w-4 h-4 text-[#F37B2A]" /> Scan
              </button>
            </div>
          </Field>
          <Field label="Manufacturer">
            <Input data-testid="input-manufacturer" value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Model">
            <Input data-testid="input-model" value={form.model} onChange={(e) => set("model", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Location">
            <Input data-testid="input-location" placeholder="e.g. HQ / Floor 3 / Desk 12" value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} />
          </Field>
        </Section>

        <Section title="Hardware">
          <Field label="Operating System">
            <Select value={form.operating_system || ""} onValueChange={(v) => set("operating_system", v)}>
              <SelectTrigger data-testid="select-operating_system" className={selectCls}>
                <SelectValue placeholder="Select OS" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-white border-[#D6CFC1]">
                {osList.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="CPU">
            <Input data-testid="input-cpu" placeholder="e.g. Intel i7-1260P" value={form.cpu} onChange={(e) => set("cpu", e.target.value)} className={inputCls} />
          </Field>
          <Field label="RAM">
            <Input data-testid="input-ram" placeholder="e.g. 16GB" value={form.ram} onChange={(e) => set("ram", e.target.value)} className={inputCls} />
          </Field>
          <Field label="HDD">
            <Input data-testid="input-hdd" placeholder="e.g. 512GB SSD" value={form.hdd} onChange={(e) => set("hdd", e.target.value)} className={inputCls} />
          </Field>
        </Section>

        <Section title="Assignment">
          <Field label="Assigned To">
            <Input data-testid="input-assigned_to" value={form.assigned_to} onChange={(e) => set("assigned_to", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Assignee Email">
            <Input data-testid="input-assigned_email" type="email" value={form.assigned_email} onChange={(e) => set("assigned_email", e.target.value)} className={inputCls} />
          </Field>
        </Section>

        <Section title="Procurement">
          <Field label="Purchase Date">
            <Input data-testid="input-purchase_date" type="date" value={form.purchase_date || ""} onChange={(e) => set("purchase_date", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Warranty Expiry">
            <Input data-testid="input-warranty_expiry" type="date" value={form.warranty_expiry || ""} onChange={(e) => set("warranty_expiry", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Purchase Price (NZD, GST excl.)">
            <Input data-testid="input-purchase_cost" type="number" min="0" step="0.01" value={form.purchase_cost} onChange={(e) => set("purchase_cost", e.target.value)} className={inputCls} />
          </Field>
        </Section>

        <Section title="Accounting">
          <Field label="Depreciation Type">
            <Select value={form.depreciation_type || ""} onValueChange={(v) => set("depreciation_type", v)}>
              <SelectTrigger data-testid="select-depreciation_type" className={selectCls}>
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-white border-[#D6CFC1]">
                {DEPRECIATION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Depreciation Rate (%)">
            <Input data-testid="input-depreciation_rate" type="number" min="0" step="0.01" value={form.depreciation_rate} onChange={(e) => set("depreciation_rate", e.target.value)} className={inputCls} />
          </Field>
          <Field label="EOY1 (NZD, GST excl.) — auto">
            <Input data-testid="input-eoy1" type="text" readOnly value={`NZ$${calculatedEoy.eoy1.toLocaleString()}`} className={`${inputCls} text-[#5A5A5A] bg-[#F0EDE3]`} />
          </Field>
          <Field label="EOY2 (NZD, GST excl.) — auto">
            <Input data-testid="input-eoy2" type="text" readOnly value={`NZ$${calculatedEoy.eoy2.toLocaleString()}`} className={`${inputCls} text-[#5A5A5A] bg-[#F0EDE3]`} />
          </Field>
          <Field label="EOY3 (NZD, GST excl.) — auto">
            <Input data-testid="input-eoy3" type="text" readOnly value={`NZ$${calculatedEoy.eoy3.toLocaleString()}`} className={`${inputCls} text-[#5A5A5A] bg-[#F0EDE3]`} />
          </Field>
          <Field label="EOY4 (NZD, GST excl.) — auto">
            <Input data-testid="input-eoy4" type="text" readOnly value={`NZ$${calculatedEoy.eoy4.toLocaleString()}`} className={`${inputCls} text-[#5A5A5A] bg-[#F0EDE3]`} />
          </Field>
        </Section>

        <Section title="Notes" cols={1}>
          <Field label="Notes" cols={1}>
            <Textarea data-testid="input-notes" rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-none bg-[#FAF7F0] border-[#D6CFC1] font-mono text-sm focus-visible:ring-1 focus-visible:ring-[#F37B2A] focus-visible:ring-offset-0" />
          </Field>
        </Section>

        {error && <div data-testid="form-error" className="border border-[#B91C1C]/40 bg-[rgba(185,28,28,0.08)] text-[#B91C1C] px-3 py-2 text-xs font-mono">{error}</div>}

        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 pt-2 border-t border-[#E5E0D5]">
          <Button type="submit" data-testid="submit-asset-btn" disabled={saving} className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-[0.2em] uppercase h-12 sm:h-10 flex-1 sm:flex-none">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Register Asset"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cancel-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:text-[#0F0F0F] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase h-12 sm:h-10">
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "rounded-none bg-[#FAF7F0] border-[#D6CFC1] font-mono text-base sm:text-sm focus-visible:ring-1 focus-visible:ring-[#F37B2A] focus-visible:ring-offset-0 h-11 sm:h-10";
const selectCls = "rounded-none bg-[#FAF7F0] border-[#D6CFC1] font-mono text-base sm:text-sm h-11 sm:h-10";

function Section({ title, children, cols = 2 }) {
  return (
    <div className="border border-[#E5E0D5] bg-white">
      <div className="px-4 sm:px-6 py-3 border-b border-[#E5E0D5]">
        <div className="uppercase-label">{title}</div>
      </div>
      <div className={`p-4 sm:p-6 grid grid-cols-1 md:grid-cols-${cols} gap-4 sm:gap-5`}>{children}</div>
    </div>
  );
}

function Field({ label, children, cols }) {
  return (
    <div className={cols === 1 ? "md:col-span-2" : ""}>
      <Label className="uppercase-label">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
